import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { ChevronRight, Award, TrendingUp, Shield, Users, Star, ArrowRight, Package, Clock, Sparkles } from 'lucide-react';
import ProductCard from './ProductCard';
import axios from 'axios';
import { observeNewElements } from '../utils/scrollAnimations';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Spotlight Product Section Component
const SpotlightSection = ({ addToCart }) => {
  const [spotlightProduct, setSpotlightProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSpotlightProduct = async () => {
      try {
        const response = await axios.get(`${API}/api/products/spotlight`);
        setSpotlightProduct(response.data);
      } catch (error) {
        console.error('Error fetching spotlight product:', error);
        // Don't show anything if no spotlight product exists
      } finally {
        setLoading(false);
        // Reinitialize animations after content loads
        observeNewElements();
      }
    };

    fetchSpotlightProduct();
  }, []);

  // Don't render section if no spotlight product or still loading
  if (loading || !spotlightProduct) {
    return null;
  }

  return (
    <section className="py-20 bg-gradient-to-br from-brand-purple to-brand-purple-dark text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 fade-up">
          <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full border border-white/30 mb-4">
            <Sparkles className="w-4 h-4 text-white" />
            <span className="text-sm font-medium text-white">Featured Right Now</span>
          </div>
          <h2 className="text-5xl font-bold mb-4">Spotlight Drop</h2>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            The one pair you need to see. Limited stock, maximum heat.
          </p>
        </div>

        {/* Spotlight Product Card */}
        <div className="max-w-5xl mx-auto fade-up">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Product Image */}
              <div className="relative bg-gray-50 p-8 md:p-12 flex items-center justify-center">
                <div className="absolute top-4 right-4 bg-brand-purple text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg z-10">
                  SPOTLIGHT
                </div>
                <img
                  src={spotlightProduct.image_urls?.[0] || '/placeholder.png'}
                  alt={spotlightProduct.name}
                  className="w-full max-w-md h-auto object-contain transform hover:scale-105 transition-transform duration-500"
                />
              </div>

              {/* Product Details */}
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="mb-6">
                  <div className="text-sm font-semibold text-brand-purple mb-2 uppercase tracking-wider">
                    {spotlightProduct.brand}
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                    {spotlightProduct.name}
                  </h3>
                  
                  {/* Description with 4-line clamp and fade effect */}
                  <div className="relative">
                    <p 
                      className="text-gray-600 text-lg leading-relaxed"
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: '1.75rem',
                        maxHeight: '7rem' /* 4 lines * 1.75rem */
                      }}
                    >
                      {spotlightProduct.description}
                    </p>
                    {/* Fade overlay - only shows if text is long enough to be clamped */}
                    <div 
                      className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none"
                      style={{
                        background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)'
                      }}
                      aria-hidden="true"
                    />
                  </div>
                  
                  {/* Learn more link */}
                  <Link 
                    to={`/product/${spotlightProduct.id}`}
                    className="inline-block mt-2 text-brand-purple text-sm font-medium hover:underline focus:underline focus:outline-none transition-all"
                    aria-label={`Learn more about ${spotlightProduct.name}`}
                  >
                    Learn more
                  </Link>
                </div>

                <div className="mb-8">
                  <div className="flex items-baseline gap-3 mb-4">
                    <span className="text-4xl font-bold text-gray-900">
                      ${spotlightProduct.price}
                    </span>
                    {spotlightProduct.originalPrice && (
                      <span className="text-xl text-gray-400 line-through">
                        ${spotlightProduct.originalPrice}
                      </span>
                    )}
                  </div>

                  {/* Available Sizes */}
                  {spotlightProduct.availableSizes && spotlightProduct.availableSizes.length > 0 && (
                    <div className="mb-6">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Available Sizes</div>
                      <div className="flex flex-wrap gap-2">
                        {spotlightProduct.availableSizes.slice(0, 8).map((size, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium"
                          >
                            {size}
                          </span>
                        ))}
                        {spotlightProduct.availableSizes.length > 8 && (
                          <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium">
                            +{spotlightProduct.availableSizes.length - 8} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    asChild
                    size="lg"
                    className="bg-brand-purple hover:bg-brand-purple-dark text-white px-8 py-6 rounded-xl text-lg font-semibold transition-all duration-300 hover:scale-105 flex-1"
                  >
                    <Link to={`/product/${spotlightProduct.id}`}>
                      View Full Details
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </Button>
                </div>

                {/* Trust Badge */}
                <div className="mt-6 flex items-center gap-2 text-sm text-gray-500">
                  <Shield className="w-4 h-4" />
                  <span>100% Authentic • Verified by BX KICKS</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const HomePage = ({ addToCart }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Fetch ALL new arrivals from the dedicated endpoint (no limit)
        const response = await axios.get(`${API}/api/products/new-arrivals?limit=100`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching new arrivals:', error);
      } finally {
        setLoading(false);
        // Reinitialize animations after products load
        observeNewElements();
      }
    };

    fetchProducts();
  }, []);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    console.log('Newsletter signup:', email);
    setEmail('');
  };

  const features = [
    {
      icon: Shield,
      title: 'Bronx Roots',
      description: 'Built in the Bronx, for the community. Local pickup, real connections, no middleman tax.'
    },
    {
      icon: Package,
      title: 'Verified Always',
      description: 'Every pair legit-checked. No reps, no shortcuts. Just authentic heat you can trust.'
    },
    {
      icon: Users,
      title: 'Community First',
      description: 'Real collectors. Fair prices. Tag @bxkicks_28 to get featured and connect with the culture.'
    }
  ];

  const stats = [
    { number: '10K+', label: 'Pairs Moved', icon: Package },
    { number: '100%', label: 'Authentic', icon: Shield },
    { number: 'Local', label: 'Bronx Pickup', icon: Users },
    { number: '2-3 Days', label: 'Fast Ship', icon: Clock }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-b from-gray-50 to-white overflow-hidden min-h-[80vh] flex items-center">
        {/* Video Background */}
        <video 
          autoPlay 
          muted 
          loop 
          playsInline 
          id="heroVideo"
          className="absolute top-0 left-0 w-full h-full object-cover object-center z-0"
          style={{
            filter: 'brightness(0.7)'
          }}
        >
          <source src="https://res.cloudinary.com/dm8rx6sks/video/upload/v1762204573/sneaker-store/branding/hero-video.mp4" type="video/mp4" />
        </video>

        {/* Dark Overlay Gradient */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(0,0,0,0.6))'
          }}
        ></div>
        
        <div className="relative z-10 text-center max-w-4xl mx-auto px-4 py-20 hero-content">
          <div className="inline-flex items-center space-x-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full border border-white/30 mb-8 fade-in">
            <Award className="w-4 h-4 text-brand-purple" />
            <span className="text-sm font-medium text-brand-purple">Verified by the Bronx</span>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight drop-shadow-2xl">
            Your Bronx Plug
            <span className="bg-gradient-to-r from-brand-purple to-brand-purple-dark bg-clip-text text-transparent block">
              for Real Sneakers
            </span>
          </h1>
          
          <p className="text-xl text-white/90 mb-12 max-w-2xl mx-auto leading-relaxed drop-shadow-lg">
            Verified pairs. Fair prices. Built in the BX by Paul Arzola—lifelong collector, certified reseller, no games.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-brand-purple hover:bg-brand-purple-dark text-white px-8 py-6 rounded-xl text-lg font-semibold transition-all duration-300 hover:scale-105 hover:shadow-xl"
              data-testid="shop-collection-button"
            >
              <Link to="/collection">
                Shop the Heat
                <ChevronRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            
            <Button 
              asChild
              variant="outline" 
              size="lg"
              className="border-2 border-white/80 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 px-8 py-6 rounded-xl text-lg font-semibold transition-all duration-300"
              data-testid="explore-brands-button"
            >
              <Link to="/about">
                Our Story
              </Link>
            </Button>
          </div>
        </div>

        {/* Mobile-specific video styles */}
        <style jsx>{`
          @media (min-width: 768px) {
            #heroVideo {
              object-fit: cover;
              object-position: center;
              transform: scale(1.3);
            }
          }

          @media (max-width: 767px) {
            #heroVideo {
              object-fit: contain;
              width: 100vw;
              height: 100vh;
              transform: none;
            }
          }
        `}</style>
      </div>

      {/* Stats Section */}
      <section className="py-12 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center fade-up stagger-item">
                  <div className="flex justify-center mb-2">
                    <Icon className="w-6 h-6 text-brand-purple" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">{stat.number}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why BX KICKS Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-up">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why BX KICKS</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real sneakers, real people, real respect.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index} 
                  className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 fade-up stagger-item"
                >
                  <div className="w-14 h-14 bg-brand-purple/10 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-brand-purple" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-white" id="featured">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-3">New Arrivals</h2>
              <p className="text-gray-600 text-lg">Fresh drops weekly. Local pickup or fast ship nationwide.</p>
            </div>
            <Button 
              asChild
              className="bg-brand-purple hover:bg-brand-purple-dark text-white hidden md:block"
            >
              <Link to="/collection">
                See All
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 aspect-square rounded-2xl mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="relative">
              {/* Scroll Container */}
              <div 
                id="products-scroll-container"
                className="flex gap-8 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
                style={{
                  scrollSnapType: 'x mandatory',
                  WebkitOverflowScrolling: 'touch'
                }}
              >
                {products.map((product) => (
                  <div 
                    key={product.id}
                    className="flex-none w-[280px] scroll-snap-align-start"
                    style={{ scrollSnapAlign: 'start' }}
                  >
                    <ProductCard
                      product={product}
                      onAddToCart={addToCart}
                    />
                  </div>
                ))}
              </div>

              {/* Navigation Buttons */}
              {products.length > 3 && (
                <>
                  <button
                    onClick={() => {
                      const container = document.getElementById('products-scroll-container');
                      container.scrollBy({ left: -320, behavior: 'smooth' });
                    }}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white shadow-xl rounded-full p-3 hover:bg-gray-50 transition-all duration-300 z-10 hidden md:block"
                    aria-label="Scroll left"
                  >
                    <ChevronRight className="w-6 h-6 text-gray-900 rotate-180" />
                  </button>
                  <button
                    onClick={() => {
                      const container = document.getElementById('products-scroll-container');
                      container.scrollBy({ left: 320, behavior: 'smooth' });
                    }}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white shadow-xl rounded-full p-3 hover:bg-gray-50 transition-all duration-300 z-10 hidden md:block"
                    aria-label="Scroll right"
                  >
                    <ChevronRight className="w-6 h-6 text-gray-900" />
                  </button>
                </>
              )}

              {/* Mobile: See All Button */}
              <div className="mt-8 text-center md:hidden">
                <Button 
                  asChild
                  className="bg-brand-purple hover:bg-brand-purple-dark text-white w-full"
                >
                  <Link to="/collection">
                    See All
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Hide scrollbar styles */}
        <style jsx>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>
      </section>

      {/* Spotlight Product Section */}
      <SpotlightSection addToCart={addToCart} />

      {/* Newsletter Section */}
      <section className="py-20 bg-brand-purple text-white" data-testid="newsletter-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Join the List</h2>
          <p className="text-xl text-gray-200 mb-8">
            Early drops, local events, restock alerts. Real updates, no spam.
          </p>
          
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              className="flex-1 px-6 py-4 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
              required
              data-testid="newsletter-email-input"
            />
            <Button 
              type="submit" 
              className="bg-white text-brand-purple hover:bg-gray-100 px-8 py-4 rounded-xl font-semibold"
              data-testid="newsletter-submit-button"
            >
              Lock In
            </Button>
          </form>
          
          <p className="mt-6 text-sm text-gray-300">
            Follow us: <a href="https://www.instagram.com/bxkicks_28/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">@bxkicks_28</a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;